import { alpha, createTheme } from '@mui/material/styles'

const font =
  "'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"

// — Palette —
const brand = {
  50: '#ECFDF5',
  100: '#D1FAE5',
  200: '#A7F3D0',
  300: '#6EE7B7',
  400: '#34D399',
  500: '#10B981',
  600: '#059669',
  700: '#047857',
  800: '#065F46',
  900: '#064E3B',
}

const slate = {
  50: '#F8FAFC',
  100: '#F1F5F9',
  200: '#E2E8F0',
  300: '#CBD5E1',
  400: '#94A3B8',
  500: '#64748B',
  600: '#475569',
  700: '#334155',
  800: '#1E293B',
  900: '#0F172A',
  950: '#020617',
}

const r = { xs: 4, sm: 8, md: 12, lg: 16, xl: 20 }

// Shared shadow language
const shadow = {
  xs: '0 1px 2px rgba(0,0,0,0.04)',
  sm: `0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.03)`,
  md: `0 4px 12px rgba(0,0,0,0.05), 0 1px 4px rgba(0,0,0,0.03)`,
  lg: `0 12px 32px rgba(0,0,0,0.08), 0 4px 8px rgba(0,0,0,0.04)`,
  xl: `0 20px 48px rgba(0,0,0,0.1), 0 8px 16px rgba(0,0,0,0.05)`,
  focus: `0 0 0 3px ${alpha(brand[600], 0.12)}`,
}

export const theme = createTheme({
  shape: { borderRadius: r.sm },
  palette: {
    primary: {
      main: brand[700],
      light: brand[500],
      dark: brand[800],
      contrastText: '#fff',
    },
    secondary: {
      main: brand[600],
      light: brand[400],
      dark: brand[700],
      contrastText: '#fff',
    },
    background: { default: '#F6F7F9', paper: '#FFFFFF' },
    text: { primary: slate[900], secondary: slate[500], disabled: slate[400] },
    divider: alpha(slate[200], 0.7),
    error: { main: '#EF4444', light: '#FCA5A5', dark: '#991B1B' },
    success: { main: brand[500], light: brand[200], dark: brand[700] },
    warning: { main: '#F59E0B', light: '#FCD34D', dark: '#D97706' },
    info: { main: '#0EA5E9', light: '#7DD3FC', dark: '#0369A1' },
    action: {
      hover: alpha(slate[900], 0.03),
      selected: alpha(brand[600], 0.06),
      focus: alpha(brand[600], 0.1),
    },
  },

  typography: {
    fontFamily: font,
    h1: { fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.1 },
    h2: { fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.15 },
    h3: { fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.2 },
    h4: { fontWeight: 600, letterSpacing: '-0.015em', lineHeight: 1.25 },
    h5: { fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.3 },
    h6: {
      fontWeight: 600,
      fontSize: '0.9375rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.4,
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: '0.9375rem',
      letterSpacing: '-0.006em',
    },
    subtitle2: {
      fontWeight: 600,
      fontSize: '0.6875rem',
      letterSpacing: '0.06em',
      textTransform: 'uppercase' as const,
      color: slate[400],
    },
    body1: { fontSize: '0.875rem', lineHeight: 1.65, color: slate[700] },
    body2: { fontSize: '0.8125rem', lineHeight: 1.6, color: slate[600] },
    caption: {
      fontSize: '0.6875rem',
      letterSpacing: '0.01em',
      color: slate[400],
      lineHeight: 1.4,
    },
    button: {
      textTransform: 'none' as const,
      fontWeight: 500,
      fontSize: '0.8125rem',
      letterSpacing: '0.01em',
    },
  },

  components: {
    // — Base —
    MuiCssBaseline: {
      styleOverrides: { body: { fontFamily: font } },
    },

    // — Buttons —
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: r.sm,
          fontWeight: 500,
          fontSize: '0.8125rem',
          padding: '6px 16px',
          transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        contained: {
          boxShadow: shadow.xs,
          '&:hover': { boxShadow: shadow.sm },
        },
        outlined: {
          borderColor: alpha(slate[300], 0.8),
          color: slate[700],
          '&:hover': {
            borderColor: slate[400],
            backgroundColor: alpha(slate[900], 0.02),
          },
        },
        text: {
          '&:hover': {
            backgroundColor: alpha(slate[900], 0.03),
          },
        },
        sizeSmall: {
          padding: '4px 10px',
          fontSize: '0.75rem',
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: r.sm,
          color: slate[500],
          transition: 'all 0.12s ease',
          '&:hover': {
            backgroundColor: alpha(slate[900], 0.04),
            color: slate[700],
          },
        },
        sizeSmall: {
          padding: 5,
        },
      },
    },

    // — Surfaces —
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        elevation0: { boxShadow: 'none' },
        elevation1: { boxShadow: shadow.sm },
        elevation2: { boxShadow: shadow.md },
        elevation8: { boxShadow: shadow.lg },
        elevation16: { boxShadow: shadow.xl },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: r.md,
          border: `1px solid ${alpha(slate[200], 0.8)}`,
          boxShadow: 'none',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            borderColor: alpha(slate[300], 0.9),
            boxShadow: `0 2px 8px ${alpha(slate[900], 0.04)}`,
          },
        },
      },
    },

    MuiCardActionArea: {
      styleOverrides: {
        root: {
          transition: 'all 0.15s ease',
          '&:hover .MuiCardActionArea-focusHighlight': {
            opacity: 0.015,
          },
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: r.xl,
          boxShadow: shadow.xl,
          border: `1px solid ${alpha(slate[200], 0.5)}`,
        },
      },
    },

    // — Inputs —
    MuiTextField: {
      defaultProps: { variant: 'outlined' },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: r.sm,
          fontSize: '0.8125rem',
          transition: 'all 0.15s ease',
          '& fieldset': {
            borderColor: alpha(slate[300], 0.7),
            transition: 'all 0.15s ease',
          },
          '&:hover fieldset': {
            borderColor: `${slate[400]} !important`,
          },
          '&.Mui-focused fieldset': {
            borderColor: `${brand[600]} !important`,
            borderWidth: '1.5px !important',
            boxShadow: shadow.focus,
          },
        },
        input: {
          fontSize: '0.8125rem',
          '&::placeholder': {
            color: slate[400],
            opacity: 1,
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        select: {
          fontSize: '0.8125rem',
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          color: slate[500],
        },
      },
    },

    // — Data display —
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: r.xs,
          fontWeight: 500,
          fontSize: '0.7rem',
          height: 24,
        },
        outlined: {
          borderColor: alpha(slate[300], 0.7),
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: slate[800],
          fontSize: '0.7rem',
          fontWeight: 400,
          borderRadius: r.sm,
          padding: '5px 10px',
          boxShadow: shadow.md,
          letterSpacing: '0.01em',
        },
        arrow: { color: slate[800] },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: r.sm,
          fontSize: '0.8125rem',
          fontWeight: 400,
        },
      },
    },

    MuiBadge: {
      styleOverrides: {
        badge: {
          fontSize: '0.625rem',
          fontWeight: 600,
        },
      },
    },

    // — Tables —
    MuiTable: {
      styleOverrides: {
        root: { borderCollapse: 'separate', borderSpacing: 0 },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            backgroundColor: '#FAFBFC',
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${alpha(slate[200], 0.6)}`,
          padding: '8px 12px',
          fontSize: '0.8125rem',
          color: slate[700],
        },
        head: {
          fontWeight: 600,
          fontSize: '0.65rem',
          textTransform: 'uppercase',
          letterSpacing: '0.07em',
          color: slate[400],
          backgroundColor: '#FAFBFC',
          borderBottom: `1px solid ${alpha(slate[200], 0.8)}`,
          padding: '10px 12px',
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.1s ease',
          '&:hover': {
            backgroundColor: alpha(slate[100], 0.5),
          },
        },
      },
    },

    // — Navigation —
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 400,
          fontSize: '0.8125rem',
          letterSpacing: '0.005em',
          minHeight: 40,
          padding: '6px 14px',
          color: slate[500],
          borderRadius: `${r.sm}px ${r.sm}px 0 0`,
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '& .MuiSvgIcon-root': {
            fontSize: '0.9rem',
            opacity: 0.45,
            transition: 'all 0.2s ease',
          },
          '&:hover': {
            color: slate[700],
            backgroundColor: alpha(slate[900], 0.02),
            '& .MuiSvgIcon-root': {
              opacity: 0.65,
            },
          },
          '&.Mui-selected': {
            fontWeight: 600,
            color: brand[700],
            '& .MuiSvgIcon-root': {
              opacity: 1,
              color: brand[600],
            },
          },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 40 },
        indicator: {
          height: 2,
          borderRadius: '2px 2px 0 0',
          backgroundColor: brand[600],
        },
        scrollButtons: {
          color: slate[400],
          '&:hover': { color: slate[700] },
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: r.sm,
          transition: 'all 0.12s ease',
          '&:hover': {
            backgroundColor: alpha(slate[900], 0.025),
          },
          '&.Mui-selected': {
            backgroundColor: alpha(brand[600], 0.05),
            '&:hover': {
              backgroundColor: alpha(brand[600], 0.07),
            },
          },
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 34,
          color: slate[400],
        },
      },
    },

    MuiListItemText: {
      styleOverrides: {
        primary: { fontSize: '0.875rem', fontWeight: 500, color: slate[700] },
        secondary: { fontSize: '0.6875rem', color: slate[400] },
      },
    },

    // — Layout —
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: alpha(slate[200], 0.6) },
      },
    },

    MuiToolbar: {
      styleOverrides: {
        dense: {
          minHeight: 48,
        },
      },
    },

    MuiAccordion: {
      styleOverrides: {
        root: {
          '&:before': { display: 'none' },
          boxShadow: 'none',
          border: `1px solid ${alpha(slate[200], 0.7)}`,
          borderRadius: `${r.md}px !important`,
          '&.Mui-expanded': { margin: 0 },
        },
      },
    },

    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: 44,
          '&.Mui-expanded': { minHeight: 44 },
        },
      },
    },

    MuiAccordionDetails: {
      styleOverrides: { root: { padding: 0 } },
    },

    // — Feedback —
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-track': {
            borderRadius: 12,
            opacity: 0.15,
            backgroundColor: slate[400],
          },
          '& .Mui-checked + .MuiSwitch-track': {
            opacity: 0.25,
          },
        },
        thumb: {
          boxShadow: shadow.xs,
        },
      },
    },

    MuiFormControlLabel: {
      styleOverrides: {
        label: { fontSize: '0.8125rem', color: slate[600] },
      },
    },

    // — Overlays —
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: r.md,
          boxShadow: shadow.lg,
          border: `1px solid ${alpha(slate[200], 0.5)}`,
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: r.md,
          boxShadow: shadow.lg,
          border: `1px solid ${alpha(slate[200], 0.5)}`,
        },
        list: {
          padding: 4,
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          borderRadius: r.xs,
          margin: '1px 0',
          padding: '6px 12px',
          transition: 'all 0.1s ease',
          '&:hover': {
            backgroundColor: alpha(slate[900], 0.03),
          },
          '&.Mui-selected': {
            backgroundColor: alpha(brand[600], 0.06),
            '&:hover': {
              backgroundColor: alpha(brand[600], 0.08),
            },
          },
        },
      },
    },

    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: brand[500],
        },
      },
    },
  },
})
